"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * The tab row every page uses for its sections: a content-width pill track, bold labels, and the
 * current tab lifted onto a card with its icon in the brand color. Exported so a row of route links
 * (`aria-current="page"`) looks exactly like a row of Radix tabs (`data-state="active"`).
 */
export const tabsListClassName =
  "inline-flex w-fit max-w-full shrink-0 items-center gap-1 overflow-x-auto rounded-full bg-muted p-1 text-muted-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

export const tabsTriggerClassName = cn(
  "inline-flex h-9 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 text-sm font-bold text-muted-foreground outline-none transition-[background-color,color,box-shadow] duration-150",
  "hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50",
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  "data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-[0_2px_8px_-2px_color-mix(in_oklch,var(--foreground)_25%,transparent)] data-[state=active]:[&>svg]:text-primary",
  "aria-[current=page]:bg-card aria-[current=page]:text-foreground aria-[current=page]:shadow-[0_2px_8px_-2px_color-mix(in_oklch,var(--foreground)_25%,transparent)] aria-[current=page]:[&>svg]:text-primary",
);

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn("flex flex-col gap-2", className)} {...props} />;
}

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List data-slot="tabs-list" className={cn(tabsListClassName, className)} {...props} />;
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return <TabsPrimitive.Trigger data-slot="tabs-trigger" className={cn(tabsTriggerClassName, className)} {...props} />;
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn("flex-1 outline-none", className)} {...props} />;
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
